import React, { useState, useEffect } from 'react';
import { generateImageVariants } from '@utils/imageUtils';

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  audioUrl?: string; // Optional: for generating partial matches from audio filename
}

/**
 * Component that loads an image with smart fallback logic.
 * Supports partial filename matching for tracks with similar names (e.g., remixes).
 *
 * Usage:
 * ```tsx
 * <ImageWithFallback
 *   src="acetates/artist-name/image.jpg"
 *   alt="Album cover art"
 *   audioUrl="acetates/artist-name/song-remix-3.mp3" // For partial matching
 * />
 * ```
 *
 * The component will:
 * 1. Try the exact specified src path
 * 2. Try partial matches based on audioUrl (e.g., "breaking-up.png" for "breaking-up-remix-3.mp3")
 * 3. Try exact filename match with .png extension
 * 4. Fall back to default placeholder image
 */
const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({ src, alt, className, style, audioUrl }) => {
  const [currentSrc, setCurrentSrc] = useState<string>('/images/acetate-placeholder.jpg');
  const [hasError, setHasError] = useState<boolean>(false);
  const [fallbackIndex, setFallbackIndex] = useState<number>(0);
  const [imageVariants, setImageVariants] = useState<string[]>([]);

  // Generate image variants when props change
  useEffect(() => {
    const variants: string[] = [];

    // Generate variants based on src and audioUrl
    if (audioUrl) {
      const generatedVariants = generateImageVariants(audioUrl, src);
      variants.push(...generatedVariants);
    } else if (src) {
      // Just use the provided src if no audioUrl
      variants.push(src);
    }

    // Normalize all paths to start with /
    const normalizedVariants = variants.map(variant =>
      variant.startsWith('/') ? variant : `/${variant}`
    );

    setImageVariants(normalizedVariants);
    setFallbackIndex(0);
    setHasError(false);

    // Set the first variant as current src
    if (normalizedVariants.length > 0) {
      setCurrentSrc(normalizedVariants[0]);
    } else {
      setCurrentSrc('/images/acetate-placeholder.jpg');
    }
  }, [src, audioUrl]);

  const handleError = () => {
    const nextIndex = fallbackIndex + 1;

    if (nextIndex < imageVariants.length) {
      // Try the next variant
      setFallbackIndex(nextIndex);
      setCurrentSrc(imageVariants[nextIndex]);
    } else if (!hasError) {
      // All variants failed, use placeholder
      setCurrentSrc('/images/acetate-placeholder.jpg');
      setHasError(true);
    }
  };

  const handleLoad = () => {
    console.log(`Successfully loaded image from: ${currentSrc}`);
  };

  return (
    <img
      src={currentSrc || '/images/acetate-placeholder.jpg'}
      alt={alt}
      className={className}
      style={style}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
};

export default ImageWithFallback;